import type { BaseConditionContext, ConditionMap } from '@connected/core';
import type { ElectricalProjectInspectionKind } from '@connected/elektra-sdk/validators/electrical-project';

import { isProjectPath } from '@connected/api-client/lib/login';
import { createConditionContext } from '@connected/core';

// ============================================================================
// Types
// ============================================================================

/**
 * Elektra-app specific condition context.
 * Extends BaseConditionContext with project-specific fields.
 */
export type ElektraConditionContext = BaseConditionContext & {
    /**
     * Check if the project has a specific inspection kind.
     * Used for visibility of solar inspection related features.
     */
    hasInspectionKind: (kind: ElectricalProjectInspectionKind) => boolean;

    /**
     * Whether the project has price quote feature enabled.
     * Controls visibility of quotation navigation and related features.
     */
    enabledElectricalProjectPriceQuote: boolean;

    /**
     * Whether the project has planning feature enabled.
     * Controls visibility of schedule/planning navigation.
     */
    enabledElectricalProjectPlanning: boolean;

    /**
     * Whether extended RISO measurements are enabled.
     * Controls visibility of the RISO tab in distribution board inspection.
     */
    enableExtendedRiso: boolean;

    /**
     * Whether the separate impedance measurements page is enabled.
     * When true, Zi/Zs measurements are shown on a dedicated page instead of the extended device card.
     */
    enableImpedanceMeasurementsPage: boolean;

    /**
     * Whether the project has any ResidualCurrentCircuitBreakerDevice devices.
     * Controls visibility of the 'Aardlekautomaten' tab for legacy projects.
     */
    hasResidualCurrentCircuitBreakerDevices: boolean;
};

// ============================================================================
// Named Conditions
// ============================================================================

/**
 * Named conditions for elektra-app.
 * These semantic names describe capabilities rather than implementation details.
 *
 * Usage:
 * - In navigation config: `condition: 'canAccessQuotation'`
 * - In components: `const canAccess = useCondition('canAccessQuotation')`
 * - In route guards: `allowedConditions: ['canAccessApp', 'canAccessSso']`
 */
export const elektraConditions = {
    // Route guard conditions
    /**
     * User can access the main app routes (authenticated + on a project path)
     */
    canAccessApp: (ctx: ElektraConditionContext) =>
        ctx.isAuthenticated && isProjectPath(ctx.currentPath),

    /**
     * User can access SSO callback routes
     */
    canAccessSso: (ctx: ElektraConditionContext) =>
        ctx.currentPath.startsWith('/sso'),

    /**
     * User can access login routes
     */
    canAccessLogin: (ctx: ElektraConditionContext) =>
        ctx.currentPath.startsWith('/login'),

    // Navigation conditions
    /**
     * User can access quotation features (price quotes)
     */
    canAccessQuotation: (ctx: ElektraConditionContext) =>
        ctx.enabledElectricalProjectPriceQuote,

    /**
     * User can access schedule/planning features
     */
    canAccessSchedule: (ctx: ElektraConditionContext) =>
        ctx.enabledElectricalProjectPlanning,

    /**
     * User can access solar inspection features (PV inverters, control points)
     */
    canAccessSolarInspection: (ctx: ElektraConditionContext) =>
        ctx.hasInspectionKind('Scope12'),

    /**
     * User can access extended RISO measurements tab
     */
    canAccessExtendedRiso: (ctx: ElektraConditionContext) =>
        ctx.enableExtendedRiso,

    /**
     * User can access the separate impedance measurements page (Zi/Zs values).
     * When true, impedance data is shown on a dedicated page.
     */
    canAccessImpedanceMeasurementsPage: (ctx: ElektraConditionContext) =>
        ctx.enableImpedanceMeasurementsPage,

    /**
     * User can access extended device card for final circuit protective devices.
     * Shows extended card only when extended RISO is enabled AND the separate impedance page is disabled.
     */
    canAccessExtendedDeviceCard: (ctx: ElektraConditionContext) =>
        ctx.enableExtendedRiso && !ctx.enableImpedanceMeasurementsPage,

    /**
     * User can access residual current circuit breakers tab (legacy devices)
     */
    canAccessResidualCurrentCircuitBreakers: (ctx: ElektraConditionContext) =>
        ctx.hasResidualCurrentCircuitBreakerDevices,
} as const satisfies ConditionMap<ElektraConditionContext>;

/**
 * Type helper for condition names.
 * Use this for type-safe condition references.
 */
export type ElektraConditionName = keyof typeof elektraConditions;

// ============================================================================
// Condition Context (React)
// ============================================================================

/**
 * Condition context factory result.
 * Provides Provider component and hooks for evaluating conditions.
 *
 * @example
 * // In a provider:
 * <ConditionProvider value={conditionContext}>
 *   <App />
 * </ConditionProvider>
 *
 * // In a component:
 * const canAccessQuotation = useCondition('canAccessQuotation');
 * const evaluate = useConditionEvaluator();
 * const isVisible = route.condition ? evaluate(route.condition) : true;
 */
export const {
    ConditionProvider,
    useConditionContext,
    useCondition,
    useConditionEvaluator,
    conditions,
} = createConditionContext<ElektraConditionContext, typeof elektraConditions>({
    conditions: elektraConditions,
});
